import { useEffect, useRef, useState } from "react";
import dropdownStyles from "../../../styles/UI/accordion.module.css";
import inputStyles from "../../../styles/UI/inputFields.module.css";
import labels from "../../../utils/labels";
import ColouredRoundWrapper from "../wrappers/ColouredRoundWrapper";

const pad2 = (n: number) => String(n).padStart(2, "0");

const buildTimeOptions = (stepMinutes: number): string[] => {
  const options: string[] = [];
  const totalMinutesInDay = 24 * 60;
  for (let m = 0; m < totalMinutesInDay; m += stepMinutes) {
    const hh = Math.floor(m / 60);
    const mm = m % 60;
    options.push(`${pad2(hh)}:${pad2(mm)}`);
  }
  return options;
};

const TimePicker = ({
  id,
  value,
  label,
  placeholder,
  onTimeChange,
  disabled,
  classes,
  helperText,
  min,
  max,
  excludedOptions,
  stepMinutes = 15,
  error,
}: {
  id: string;
  value: string;
  label?: string;
  placeholder?: string;
  onTimeChange: (newTime: string) => void;
  disabled?: boolean;
  classes?: string;
  helperText?: string;
  min?: string;
  max?: string;
  excludedOptions?: string[];
  stepMinutes?: number;
  error?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const allOptions = buildTimeOptions(stepMinutes);
  const excludedSet = excludedOptions ? new Set(excludedOptions) : null;
  // `"HH:MM"` strings are alphanumerically ordered, so plain string compare
  // is sufficient for the min/max bounds.
  const filteredOptions = allOptions.filter(
    (time) =>
      (!min || time >= min) && (!max || time <= max) && !excludedSet?.has(time),
  );
  // Ensure the currently-selected value is present even if it falls outside
  // the active filter (off-step, before min, after max, or freshly excluded
  // by a newly-added neighbouring segment).
  const normalizedOptions =
    value && !filteredOptions.includes(value)
      ? [value, ...filteredOptions]
      : filteredOptions;
  const selectedIndex = normalizedOptions.indexOf(value);

  const labelId = `${id}-label`;
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-option-${i}`;

  const openDropdown = () => {
    if (disabled) return;
    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const selectOption = (option: string) => {
    onTimeChange(option);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  // Focus the list and scroll to the focused item when it opens
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    listRef.current.focus();
    const item = listRef.current.children[focusedIndex] as HTMLElement;
    item?.scrollIntoView({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Scroll to focused item when navigation changes it
  useEffect(() => {
    if (!isOpen || !listRef.current || focusedIndex < 0) return;
    const item = listRef.current.children[focusedIndex] as HTMLElement;
    item?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex, isOpen]);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", handleOutsideClick);
  }, [isOpen]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      openDropdown();
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const last = normalizedOptions.length - 1;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, last));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        selectOption(normalizedOptions[focusedIndex]);
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown();
        break;
      case "Home":
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setFocusedIndex(last);
        break;
    }
  };

  const handleListBlur = (e: React.FocusEvent<HTMLUListElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  return (
    <div
      className={`flex gap-[var(--spacing-xs)] flex-col ${classes ?? ""}`}
      ref={containerRef}
    >
      {label && (
        <span id={labelId} className={`pl-2 ${inputStyles.label}`}>
          {label}
        </span>
      )}
      <div className="relative">
        <ColouredRoundWrapper
          bgColor={disabled ? "var(--col-surface-secondary)" : "white"}
          padding
          width="100%"
          borderColor={
            error ? "var(--col-error-4)" : "var(--col-border-primary)"
          }
          classes="flex items-center focus-within:ring-2 focus-within:ring-[var(--col-border-focus)]"
        >
          {/* Preserve popup focus during a Safari mouse click until the trigger toggles. */}
          <button
            id={id}
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listId}
            aria-labelledby={label ? labelId : undefined}
            disabled={disabled}
            onMouseDown={(event): void => event.preventDefault()}
            onClick={() => (isOpen ? closeDropdown() : openDropdown())}
            onKeyDown={handleTriggerKeyDown}
            className={`focus:outline-none flex-1 min-w-0 text-left [font:var(--font-bodyStd)] bg-transparent ${
              disabled
                ? "text-[var(--col-text-secondary)] cursor-not-allowed"
                : "text-[var(--col-text-primary)]"
            }`}
          >
            {value || (
              <span className="text-[var(--col-text-secondary)]">
                {placeholder ?? labels.selectWorkTime}
              </span>
            )}
          </button>
        </ColouredRoundWrapper>
        {isOpen && (
          <ul
            id={listId}
            ref={listRef}
            role="listbox"
            aria-labelledby={label ? labelId : undefined}
            aria-activedescendant={optionId(focusedIndex)}
            tabIndex={-1}
            onKeyDown={handleListKeyDown}
            onBlur={handleListBlur}
            className={`${dropdownStyles.accordionList} ${dropdownStyles.accordionListScroll} focus:outline-none`}
            style={{ minWidth: "100%", maxHeight: "300px" }}
          >
            {normalizedOptions.map((option, index) => (
              <li
                key={option}
                id={optionId(index)}
                role="option"
                aria-selected={option === value}
                className={`
                  ${dropdownStyles.accordionListItem}
                  ${option === value ? dropdownStyles.accordionListItem__selected : ""}
                  ${index === focusedIndex ? dropdownStyles.accordionListItem__focused : ""}
                `}
                onPointerDown={(e) => {
                  e.preventDefault();
                  selectOption(option);
                }}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
      {helperText && (
        <span className="text-[var(--col-text-secondary)]">{helperText}</span>
      )}
    </div>
  );
};

export default TimePicker;
